// Etsy kesintileri — siparis ledger'indan olculdu (Turkiye magazasi, USD satis).
// Ornek siparis: urun 22.50 + kargo 5.99 = 28.49 -> kesinti 4.32 (kurusu kurusuna):
//   transaction %6.5 (urun) + shipping_transaction %6.5 (alicinin odedigi kargo)
//   odeme isleme %3 + $0.25, regulatory operating %1.68 (urun + kargo)
//   bu uc kalemin KDV'si %20, satista yenileme (renew_sold) $0.20
// Offsite Ads (%15 + KDV) her sipariste yok; istege bagli.
// ponytail: odeme isleme alicinin satis vergisinden de %3 alir (~$0.05); vergi
// eyalete gore degistigi icin modele girmedi.

export const ETSY_FEE_MODEL = {
  transactionPct: 6.5,
  paymentPct: 3,
  paymentFixed: 0.25,
  regulatoryPct: 1.68,
  vatPct: 20,
  renewFee: 0.2,
  offsiteAdsPct: 15,
};

const r2 = (n: number) => Math.round(n * 100) / 100;
const M = ETSY_FEE_MODEL;
/** Gelirin (urun + alinan kargo) yuzde kac kesilir — KDV dahil */
const rate = (offsiteAds: boolean) =>
  ((M.transactionPct + M.paymentPct + M.regulatoryPct) * (1 + M.vatPct / 100) + (offsiteAds ? M.offsiteAdsPct * (1 + M.vatPct / 100) : 0)) / 100;
/** Siparis basina sabit kesinti — KDV dahil */
const fixed = () => M.paymentFixed * (1 + M.vatPct / 100) + M.renewFee;

export interface EtsyFeeOptions { offsiteAds?: boolean }

/** Gelirden (urun fiyati + alicidan alinan kargo) Etsy'nin kestigi kalemler */
export function etsyFeeBreakdown(revenue: number, opts: EtsyFeeOptions = {}) {
  const transaction = (revenue * M.transactionPct) / 100;
  const payment = (revenue * M.paymentPct) / 100 + M.paymentFixed;
  const regulatory = (revenue * M.regulatoryPct) / 100;
  const vat = ((transaction + payment + regulatory) * M.vatPct) / 100;
  const offsiteAds = opts.offsiteAds ? (revenue * M.offsiteAdsPct * (1 + M.vatPct / 100)) / 100 : 0;
  const renew = M.renewFee;
  return { transaction, payment, regulatory, vat, offsiteAds, renew, total: transaction + payment + regulatory + vat + offsiteAds + renew };
}

export interface EtsyProfitInput extends EtsyFeeOptions {
  /** Urunun satis fiyati (indirimden sonra alicinin odedigi) */
  price: number;
  /** Alicidan alinan kargo; ucretsiz kargoda 0 */
  shippingCharge?: number;
  /** Tedarikci urun maliyeti */
  productCost: number;
  /** Tedarikciye odenen gercek kargo — kargo ucretsiz olsa da maliyettir */
  shippingCost?: number;
  extraCost?: number;
}

/** Net kar: gelir - Etsy kesintileri - urun maliyeti - gercek kargo - diger */
export function etsyNetProfit(i: EtsyProfitInput) {
  const revenue = i.price + (i.shippingCharge ?? 0);
  const fees = etsyFeeBreakdown(revenue, i);
  const net = revenue - fees.total - i.productCost - (i.shippingCost ?? 0) - (i.extraCost ?? 0);
  return { revenue, fees, net: r2(net), marginPct: revenue > 0 ? (net / revenue) * 100 : 0 };
}

/** Hedef kara gore urun satis fiyati. Hedef: marginPct (net / gelir) ya da profit ($).
 *  gelir = (maliyet + kargo + diger + sabit kesinti + hedef $) / (1 - kesinti orani - marj)
 *  fiyat = gelir - alinan kargo. Alinan kargo az ise fark urun fiyatina biner. */
export function etsyPriceFor(i: Omit<EtsyProfitInput, "price"> & { marginPct?: number; profit?: number }): number {
  const costs = i.productCost + (i.shippingCost ?? 0) + (i.extraCost ?? 0) + fixed() + (i.profit ?? 0);
  const denom = Math.max(0.05, 1 - rate(!!i.offsiteAds) - (i.marginPct ?? 0) / 100);
  return r2(Math.max(0, costs / denom - (i.shippingCharge ?? 0)));
}

/** Varsayilan indirim: Etsy'ye liste fiyati gider, magaza %50 indirimle satar */
export const DEFAULT_DISCOUNT_PCT = 50;
/** Indirimli satis fiyatindan Etsy'ye gidecek liste fiyati */
export function listPriceFor(sale: number, discountPct = DEFAULT_DISCOUNT_PCT): number {
  return r2(sale / Math.max(0.05, 1 - discountPct / 100));
}
/** Liste fiyatindan alicinin odedigi indirimli fiyat (kesinti ve kar bunun uzerinden) */
export function discountedPrice(list: number, discountPct = DEFAULT_DISCOUNT_PCT): number {
  return r2(list * (1 - discountPct / 100));
}

/** Gelirin kesinti orani ve sabit kesinti (fiyat kurallari icin) */
export function etsyFeeRate(opts: EtsyFeeOptions = {}) {
  return { rate: rate(!!opts.offsiteAds), fixed: fixed() };
}
